import { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Fab,
  Zoom,
  Fade,
  Alert,
  Snackbar,
  Paper,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ArrowBack,
  CameraAlt,
  FlashOn,
  FlashOff,
  Refresh,
  CheckCircle,
  PhotoLibrary,
  AutoAwesome
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import ModernCardDetection from "../utils/modernCardDetection";
import makeAPIRequest from "../global/apiCall";
import { POST, UPLOAD_IMAGE, UPLOAD_MULTI_IMAGE } from "../global/apiKeys";

const ModernScanCard = () => {
  const navigate = useNavigate();
  
  // Refs
  const webcamRef = useRef(null);
  const detectionRef = useRef(null);
  const animationRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  
  // States
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [detection, setDetection] = useState({ detected: false, confidence: 0, bounds: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });
  const [autoCapture, setAutoCapture] = useState(true);
  const [cardSide, setCardSide] = useState('front'); // 'front' or 'back'
  const [capturedImages, setCapturedImages] = useState({ front: null, back: null });
  const [showContinueDialog, setShowContinueDialog] = useState(false);
  
  // Initialize detection
  useEffect(() => {
    detectionRef.current = new ModernCardDetection();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Debug state changes
  useEffect(() => {
    console.log('🔍 Debug state:', {
      cardSide,
      showContinueDialog,
      capturedImage: !!capturedImage,
      frontCaptured: !!capturedImages.front,
      backCaptured: !!capturedImages.back
    });
  }, [cardSide, showContinueDialog, capturedImage, capturedImages]);

  // Start real-time detection when camera is ready
  useEffect(() => {
    if (isCameraReady && webcamRef.current && !capturedImage) {
      startDetectionLoop();
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isCameraReady, capturedImage]);

  const startDetectionLoop = useCallback(() => {
    const detectCard = async () => {
      if (webcamRef.current?.video && !isCapturing && !capturedImage) {
        try {
          const result = await detectionRef.current.detectBusinessCard(webcamRef.current.video);
          setDetection(result);
          
          // Auto capture when card is detected with high confidence
          if (autoCapture && result.detected && result.confidence > 0.8 && !isCapturing) {
            setTimeout(() => {
              if (!isCapturing && !capturedImage) {
                captureImage();
              }
            }, 1000); // Wait 1 second for stability
          }
          
          // Update overlay
          if (overlayCanvasRef.current) {
            drawDetectionOverlay(result);
          }
        } catch (error) {
          // Silent error handling for detection
        }
      }
      
      if (!capturedImage) {
        animationRef.current = requestAnimationFrame(detectCard);
      }
    };
    
    detectCard();
  }, [autoCapture, isCapturing, capturedImage]);

  const drawDetectionOverlay = (detection) => {
    const canvas = overlayCanvasRef.current;
    const video = webcamRef.current?.video;
    
    if (!canvas || !video) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (detection.detected && detection.bounds) {
      const { bounds, confidence } = detection;
      
      // Determine color based on confidence
      const color = confidence > 0.8 ? '#00ff88' : confidence > 0.6 ? '#ffaa00' : '#ff4444';
      
      // Draw main detection rectangle
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.shadowColor = color;
      ctx.shadowBlur = 10;
      
      // Animated border
      const time = Date.now() * 0.005;
      const dashOffset = Math.sin(time) * 10;
      ctx.setLineDash([10, 5]);
      ctx.lineDashOffset = dashOffset;
      
      ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
      
      // Reset shadow and dash
      ctx.shadowBlur = 0;
      ctx.setLineDash([]);
      
      // Corner markers
      const cornerSize = 30;
      const corners = [
        { x: bounds.x, y: bounds.y },
        { x: bounds.x + bounds.width, y: bounds.y },
        { x: bounds.x, y: bounds.y + bounds.height },
        { x: bounds.x + bounds.width, y: bounds.y + bounds.height }
      ];
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 4;
      
      corners.forEach((corner, index) => {
        ctx.beginPath();
        if (index === 0) { // Top-left
          ctx.moveTo(corner.x, corner.y + cornerSize);
          ctx.lineTo(corner.x, corner.y);
          ctx.lineTo(corner.x + cornerSize, corner.y);
        } else if (index === 1) { // Top-right
          ctx.moveTo(corner.x - cornerSize, corner.y);
          ctx.lineTo(corner.x, corner.y);
          ctx.lineTo(corner.x, corner.y + cornerSize);
        } else if (index === 2) { // Bottom-left
          ctx.moveTo(corner.x, corner.y - cornerSize);
          ctx.lineTo(corner.x, corner.y);
          ctx.lineTo(corner.x + cornerSize, corner.y);
        } else { // Bottom-right
          ctx.moveTo(corner.x - cornerSize, corner.y);
          ctx.lineTo(corner.x, corner.y);
          ctx.lineTo(corner.x, corner.y - cornerSize);
        }
        ctx.stroke();
      });
      
      // Confidence indicator
      ctx.fillStyle = `rgba(${confidence > 0.8 ? '0, 255, 136' : confidence > 0.6 ? '255, 170, 0' : '255, 68, 68'}, 0.9)`;
      ctx.fillRect(bounds.x, bounds.y - 40, 200, 30);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto';
      ctx.fillText(
        `Business Card ${Math.round(confidence * 100)}%`,
        bounds.x + 10,
        bounds.y - 20
      );
    }
  };

  const captureImage = async () => {
    if (isCapturing || !webcamRef.current) return;
    
    setIsCapturing(true);
    
    try {
      // Take screenshot
      const imageSrc = webcamRef.current.getScreenshot({
        width: 1920,
        height: 1080,
        screenshotFormat: "image/jpeg",
        screenshotQuality: 0.95
      });
      
      if (!imageSrc) {
        throw new Error('Failed to capture image');
      }
      
      // Auto-crop if we have detection
      let finalImage = imageSrc;
      if (detection.detected && detection.bounds) {
        const croppedResult = await detectionRef.current.cropBusinessCard(
          webcamRef.current.video,
          detection.bounds
        );
        if (croppedResult) {
          finalImage = croppedResult.dataUrl;
        }
      }
      
      setCapturedImage(finalImage);
      setCapturedImages(prev => ({ ...prev, [cardSide]: finalImage }));
      setAlert({ open: true, message: `Business card ${cardSide} captured successfully!`, severity: 'success' });

      // Show continue dialog to ask user's choice after first side
      if (cardSide === 'front') {
        console.log('📱 Front side captured, showing continue dialog');
        setShowContinueDialog(true);
      }
      
    } catch (error) {
      setAlert({ open: true, message: 'Failed to capture image', severity: 'error' });
    } finally {
      setIsCapturing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setCapturedImages(prev => ({ ...prev, [cardSide]: null }));
    setDetection({ detected: false, confidence: 0, bounds: null });
    detectionRef.current.reset();
    setShowContinueDialog(false);
    startDetectionLoop();
  };

  const switchCardSide = (side) => {
    setCardSide(side);
    setCapturedImage(capturedImages[side]);
    setShowContinueDialog(false);
    if (!capturedImages[side]) {
      startDetectionLoop();
    }
  };

  const resetAllCaptures = () => {
    setCapturedImage(null);
    setCapturedImages({ front: null, back: null });
    setCardSide('front');
    setShowContinueDialog(false);
    setDetection({ detected: false, confidence: 0, bounds: null });
    detectionRef.current.reset();
    startDetectionLoop();
  };

  const handleContinueWithFrontOnly = () => {
    setShowContinueDialog(false);
    // Process the card with front side only
    uploadImage();
  };

  const handleScanBackSide = () => {
    setShowContinueDialog(false);
    setCardSide('back');
    setCapturedImage(null);
    setDetection({ detected: false, confidence: 0, bounds: null });
    detectionRef.current.reset();
    startDetectionLoop();
  };

  const uploadImage = async () => {
    if (!capturedImage && !capturedImages.front && !capturedImages.back) return;

    setIsProcessing(true);
    setAlert({ open: true, message: 'Processing business card...', severity: 'info' });

    try {
      let result;

      // Check if we have both sides or just one
      if (capturedImages.front && capturedImages.back) {
        // Use multi-side API
        const formData = new FormData();

        // Convert front image to blob
        const frontResponse = await fetch(capturedImages.front);
        const frontBlob = await frontResponse.blob();
        formData.append('frontImage', frontBlob, `business-card-front-${Date.now()}.jpg`);

        // Convert back image to blob
        const backResponse = await fetch(capturedImages.back);
        const backBlob = await backResponse.blob();
        formData.append('backImage', backBlob, `business-card-back-${Date.now()}.jpg`);

        result = await makeAPIRequest(POST, UPLOAD_MULTI_IMAGE, formData);

      } else if (capturedImages.front || capturedImages.back) {
        // Use single-side API
        const imageToUpload = capturedImages.front || capturedImages.back;
        const response = await fetch(imageToUpload);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append('image', blob, `business-card-${Date.now()}.jpg`);

        result = await makeAPIRequest(POST, UPLOAD_IMAGE, formData);
      } else {
        // Fallback to captured image
        const response = await fetch(capturedImage);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append('image', blob, `business-card-${Date.now()}.jpg`);

        result = await makeAPIRequest(POST, UPLOAD_IMAGE, formData);
      }

      // Prepare data with both sides if available
      const cardData = {
        ...result?.data,
        ...result?.combinedData, // For multi-side results
        frontImage: capturedImages.front,
        backImage: capturedImages.back,
        frontImageUrl: result?.frontImageUrl,
        backImageUrl: result?.backImageUrl,
        hasBothSides: !!(capturedImages.front && capturedImages.back)
      };

      // Save result and navigate
      localStorage.setItem('uploadedFileData', JSON.stringify(cardData));

      // Check for duplicate phone numbers and show appropriate message
      const duplicates = result?.duplicatePhoneContacts || result?.data?.duplicatePhoneContacts || [];
      if (duplicates.length > 0) {
        setAlert({
          open: true,
          message: `⚠️ Business card processed! Found ${duplicates.length} existing contact(s) with similar phone number(s).`,
          severity: 'warning'
        });
      } else {
        const message = capturedImages.front && capturedImages.back
          ? 'Business cards (both sides) processed successfully!'
          : 'Business card processed successfully!';
        setAlert({ open: true, message, severity: 'success' });
      }

      setTimeout(() => {
        navigate('/info');
      }, 1500);

    } catch (error) {
      setAlert({ open: true, message: 'Failed to process business card', severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGalleryUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#000',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
        p: { xs: 1, sm: 2 },
        pt: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ 
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: '#00ff88', fontSize: 20 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                Scan Business Card
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: cardSide === 'front' ? '#00ff88' : '#ffaa00',
                  fontWeight: 500,
                  textTransform: 'capitalize'
                }}
              >
                {cardSide} Side
              </Typography>
            </Box>
          </Box>
          
          <IconButton
            onClick={() => setIsFlashOn(!isFlashOn)}
            sx={{ 
              color: isFlashOn ? '#ffaa00' : 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            {isFlashOn ? <FlashOn /> : <FlashOff />}
          </IconButton>
        </Box>
        
        {/* Status indicator */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!capturedImage && (
            <Fade in timeout={600}>
              <Paper sx={{ 
                px: 3, 
                py: 1, 
                background: detection.detected 
                  ? 'linear-gradient(135deg, #00ff88, #00cc66)' 
                  : 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 600,
                    textAlign: 'center'
                  }}
                >
                  {detection.detected 
                    ? `Business Card Detected (${Math.round(detection.confidence * 100)}%)`
                    : 'Position business card in frame'
                  }
                </Typography>
              </Paper>
            </Fade>
          )}
        </Box>
      </Box>

      {/* Camera/Image View */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!capturedImage ? (
          <>
            {/* Camera */}
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              screenshotQuality={0.95}
              videoConstraints={{
                facingMode: "environment",
                width: { min: 1280, ideal: 1920, max: 2560 },
                height: { min: 720, ideal: 1080, max: 1440 }
              }}
              onUserMedia={() => setIsCameraReady(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />

            {/* Detection overlay */}
            <canvas
              ref={overlayCanvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10
              }}
            />

            {/* Scanning guide overlay with outside blur */}
            {!detection.detected && (
              <>
                {/* Top blur area */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: 'calc(50% - 150px)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(8px)',
                  zIndex: 2,
                }} />

                {/* Bottom blur area */}
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: 'calc(50% - 150px)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(8px)',
                  zIndex: 2,
                }} />

                {/* Left blur area */}
                <Box sx={{
                  position: 'absolute',
                  top: 'calc(50% - 150px)',
                  left: 0,
                  width: 'calc(50% - 200px)',
                  height: '300px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(8px)',
                  zIndex: 2,
                }} />

                {/* Right blur area */}
                <Box sx={{
                  position: 'absolute',
                  top: 'calc(50% - 150px)',
                  right: 0,
                  width: 'calc(50% - 200px)',
                  height: '300px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(8px)',
                  zIndex: 2,
                }} />

                {/* Clear scanning frame - empty border only */}
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '400px',
                  height: '300px',
                  border: '3px dashed rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  background: 'transparent',
                  zIndex: 3,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': {
                      borderColor: 'rgba(255, 255, 255, 0.9)',
                      transform: 'translate(-50%, -50%) scale(1)',
                    },
                    '50%': {
                      borderColor: cardSide === 'front' ? 'rgba(0, 255, 136, 1)' : 'rgba(255, 170, 0, 1)',
                      transform: 'translate(-50%, -50%) scale(1.02)',
                    },
                    '100%': {
                      borderColor: 'rgba(255, 255, 255, 0.9)',
                      transform: 'translate(-50%, -50%) scale(1)',
                    }
                  }
                }} />

                {/* Instructions text positioned below the frame */}
                <Box sx={{
                  position: 'absolute',
                  top: 'calc(50% + 180px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  zIndex: 4
                }}>
                  <CameraAlt sx={{
                    color: cardSide === 'front' ? '#00ff88' : '#ffaa00',
                    fontSize: 48,
                    opacity: 0.8,
                    mb: 1
                  }} />
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                    Position {cardSide} side
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Keep card steady in frame
                  </Typography>
                </Box>
              </>
            )}
          </>
        ) : (
          /* Captured Image View */
          <Zoom in timeout={500}>
            <Box sx={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}>
              <img
                src={capturedImage}
                alt="Captured business card"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Zoom>
        )}
      </Box>

      {/* Bottom Controls */}
      <Box sx={{ 
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 70%, transparent 100%)',
        p: { xs: 2, sm: 3 },
        pb: { xs: 3, sm: 4 }
      }}>
        {!capturedImage ? (
          /* Camera Controls */
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            {/* Gallery Button */}
            <input
              accept="image/*"
              id="gallery-upload"
              type="file"
              hidden
              onChange={handleGalleryUpload}
            />
            <label htmlFor="gallery-upload">
              <IconButton
                component="span"
                sx={{
                  width: 56,
                  height: 56,
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.25)',
                  }
                }}
              >
                <PhotoLibrary />
              </IconButton>
            </label>
            
            {/* Capture Button */}
            <Fab
              onClick={captureImage}
              disabled={isCapturing}
              sx={{
                width: 80,
                height: 80,
                background: detection.detected 
                  ? 'linear-gradient(135deg, #00ff88, #00cc66)'
                  : 'linear-gradient(135deg, #ffffff, #f0f0f0)',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 0 30px rgba(0, 255, 136, 0.5)'
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isCapturing ? (
                <Box sx={{ width: 24, height: 24 }}>
                  <LinearProgress 
                    sx={{ 
                      width: '100%', 
                      borderRadius: 2,
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #00ff88, #00cc66)'
                      }
                    }} 
                  />
                </Box>
              ) : (
                <CameraAlt sx={{ fontSize: 36, color: detection.detected ? 'white' : '#666' }} />
              )}
            </Fab>
            
            {/* Auto Capture Toggle */}
            <IconButton
              onClick={() => setAutoCapture(!autoCapture)}
              sx={{
                width: 56,
                height: 56,
                background: autoCapture 
                  ? 'linear-gradient(135deg, #00ff88, #00cc66)'
                  : 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: autoCapture 
                    ? 'linear-gradient(135deg, #00cc66, #00aa55)'
                    : 'rgba(255, 255, 255, 0.25)',
                }
              }}
            >
              <AutoAwesome />
            </IconButton>

            {/* Test button for debugging */}
            <Button
              onClick={() => {
                console.log('🧪 Test button clicked - triggering dialog');
                setShowContinueDialog(true);
              }}
              variant="outlined"
              size="small"
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.75rem'
              }}
            >
              Test Dialog
            </Button>
          </Box>
        ) : (
          /* Captured Image Controls */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Card side indicators */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: '12px',
                background: capturedImages.front
                  ? 'linear-gradient(135deg, #00ff88, #00cc66)'
                  : 'rgba(255, 255, 255, 0.2)',
                border: cardSide === 'front' ? '2px solid #00ff88' : '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  Front {capturedImages.front ? '✓' : '○'}
                </Typography>
              </Box>
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: '12px',
                background: capturedImages.back
                  ? 'linear-gradient(135deg, #ffaa00, #ff8800)'
                  : 'rgba(255, 255, 255, 0.2)',
                border: cardSide === 'back' ? '2px solid #ffaa00' : '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                  Back {capturedImages.back ? '✓' : '○'}
                </Typography>
              </Box>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              {(capturedImages.front || capturedImages.back) && (
                <Button
                  onClick={() => switchCardSide(cardSide === 'front' ? 'back' : 'front')}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  Switch Side
                </Button>
              )}

              <Button
                onClick={retakePhoto}
                variant="outlined"
                startIcon={<Refresh />}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                Retake
              </Button>

              <Button
                onClick={uploadImage}
                disabled={isProcessing}
                variant="contained"
                startIcon={isProcessing ? null : <CheckCircle />}
                sx={{
                  px: 4,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #00ff88, #00cc66)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00cc66, #00aa55)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                {isProcessing ? 'Processing...' : `Process Card${capturedImages.front && capturedImages.back ? 's' : ''}`}
              </Button>
            </Box>

            {/* Reset all button */}
            {(capturedImages.front || capturedImages.back) && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  onClick={resetAllCaptures}
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: 'white',
                    }
                  }}
                >
                  Reset All
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Continue Dialog */}
      {console.log('🖼️ Rendering dialog with showContinueDialog:', showContinueDialog)}
      <Dialog
        open={showContinueDialog}
        onClose={() => {
          console.log('🔒 Dialog closed by user');
          setShowContinueDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden',
            zIndex: 9999 // Ensure it's on top
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)', // More opaque backdrop
              zIndex: 9998
            }
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pb: 1,
          background: 'linear-gradient(135deg, #00ff88, #00cc66)',
          color: 'white',
          fontWeight: 600,
          fontSize: '1.25rem'
        }}>
          Front Side Captured!
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            {/* Success Icon */}
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00ff88, #00cc66)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 25px rgba(0, 255, 136, 0.3)'
            }}>
              <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
              Great! Front side captured successfully
            </Typography>

            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
              Would you like to scan the back side for additional information, or continue with just the front?
            </Typography>
          </Box>

          {/* Preview of captured front */}
          {capturedImages.front && (
            <Box sx={{
              width: '100%',
              maxWidth: 200,
              aspectRatio: '1.586/1',
              borderRadius: '12px',
              overflow: 'hidden',
              margin: '0 auto 20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '2px solid #00ff88'
            }}>
              <img
                src={capturedImages.front}
                alt="Front side preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          pt: 0,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': { flex: { xs: 1, sm: 'initial' } }
        }}>
          <Button
            onClick={handleContinueWithFrontOnly}
            variant="outlined"
            size="large"
            sx={{
              borderColor: '#00ff88',
              color: '#00ff88',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              '&:hover': {
                borderColor: '#00cc66',
                color: '#00cc66',
                background: 'rgba(0, 255, 136, 0.05)'
              }
            }}
          >
            Continue with Front Only
          </Button>

          <Button
            onClick={handleScanBackSide}
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #ffaa00, #ff8800)',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(255, 170, 0, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff8800, #ff6600)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(255, 170, 0, 0.4)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Scan Back Side
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            background: alert.severity === 'success'
              ? 'rgba(0, 255, 136, 0.9)'
              : alert.severity === 'error'
              ? 'rgba(255, 68, 68, 0.9)'
              : 'rgba(255, 255, 255, 0.9)'
          }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModernScanCard;